import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * apiClient.js - VERIFIED ARCHITECTURAL INTEGRITY
 * The Centralized AI Wrapper for JobVantage.
 * RE-INTEGRATED: Adaptive Engine ROI Logic & Granular Payload Handling.
 */

const callGeminiTextApi = httpsCallable(functions, 'callGeminiTextApi');
const callGeminiFlashApi = httpsCallable(functions, 'callGeminiFlashApi');
const calculateMarketRealityFn = httpsCallable(functions, 'calculateMarketReality');
const runCareerSimulationFn = httpsCallable(functions, 'runCareerSimulation');

/**
 * executes an httpsCallable with a single retry for transient errors.
 */
const executeWithRetry = async (callableFn, payload, systemInstruction, isRetry = false) => {
  try {
    const result = await callableFn({ 
      payload: payload, 
      systemInstruction: systemInstruction 
    });
    
    // VALIDATION: Ensure the Adaptive Engine receives the full raw data object
    if (!result.data) {
        throw new Error("EMPTY_ADAPTIVE_PAYLOAD");
    }

    return result.data;

  } catch (e) {
    const isTransient = e.code === 'deadline-exceeded' || e.code === 'unavailable';

    if (isTransient && !isRetry) {
      console.warn(`[Adaptive Engine] Transient jitter ${e.code}. Retrying...`);
      return executeWithRetry(callableFn, payload, systemInstruction, true);
    }

    // Adaptive Error Mapping for the Student/Dean UI
    switch (e.code) {
      case 'unauthenticated':
        throw new Error("Launchpad session expired. Re-authentication required.");
      case 'resource-exhausted':
        throw new Error("Campus servers are busy (Quota). Please wait 60 seconds.");
      case 'deadline-exceeded':
        throw new Error("AI reasoning timed out. Attempting simpler ROI analysis next.");
      case 'permission-denied':
        throw new Error("Security Alert: Unauthorized curriculum access attempt.");
      case 'internal':
        throw new Error("Adaptive Engine Logic Mismatch: Check JSON Schema integrity.");
      default:
        console.error(`[ARCHITECTURAL_BREAK] ${e.code}: ${e.message}`);
        throw new Error("The Curriculum Engine is stabilizing. Please try again.");
    }
  }
};

export const callApi = async (payload, systemInstruction) => {
  return executeWithRetry(callGeminiTextApi, payload, systemInstruction);
};

export const callFlashApi = async (payload, systemInstruction) => {
  try {
    return await executeWithRetry(callGeminiFlashApi, payload, systemInstruction);
  } catch (e) {
    // Client-Side Safety Net for Flash-based gamified chat loops
    const isRateLimit = e.message.includes("Quota") || e.message.includes("timed out") || e.message.includes("stabilizing");
    if (isRateLimit) {
        console.warn("[Flash Endpoint Fallback] Client-side safety net triggered.");
        return {
            reply: "... (Opponent is typing... Network congested)",
            analysis: {
                score_delta: 0,
                tactic_used: "System Delay",
                coach_tip: "The AI is processing under heavy load. Hold steady."
            }
        };
    }
    throw e;
  }
};

export const calculateMarketReality = async (payload) => {
  return executeWithRetry(calculateMarketRealityFn, payload, null);
};

export const runCareerSimulation = async (payload) => {
  try {
    const result = await runCareerSimulationFn(payload);
    if (!result.data) throw new Error("EMPTY_SIMULATION_PAYLOAD");
    return result.data;
  } catch (e) {
    const isRateLimit = e.code === 'resource-exhausted' || e.code === 'unavailable' || e.code === 'deadline-exceeded';
    if (isRateLimit) {
        console.warn(`[Simulation Safety Net] Endpoint congested (${e.code}). Injecting localized fallback.`);
        
        // Universal Fallback matching both SPARRING and COACHING schemas
        return {
            reply: "Give me a second to review that... (Opponent is typing...)",
            tactics: [{ label: "Delay", script: "Just a moment..." }],
            likely_trap: "Bandwidth delay. Wait for the opponent to respond.",
            analysis: {
                score_delta: 0,
                tactic_used: "Bandwidth Delay",
                coach_tip: "Traffic spike. The opponent is processing your request."
            }
        };
    }
    console.error(`[Simulation Error] ${e.code}: ${e.message}`);
    throw e;
  }
};